"use client";

import { useMemo, useState } from "react";
import type { CandidateProfile } from "@/lib/api/types";
import { SectionBody, SectionCard } from "../section-layout";
import { CandidateContactRevealTrigger } from "@/features/candidate/components/profile/contact/reveal-trigger";
import { ContactRevealConfirmAction } from "@/features/candidate/components/profile/contact/actions/reveal-confirm-action";
import { ContactReviewModal } from "@/features/candidate/components/profile/contact/actions/review-modal";
import ContactLayout from "@/features/candidate/components/profile/contact/layout";
import { ContactManualAdd } from "@/features/candidate/components/profile/contact/manual";
import {
  useProfileContacts,
  useRevealContact,
} from "@/hooks/use-contact-reveal";
import { Button } from "@/components/ui/button";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import flags from "react-phone-number-input/flags";
import { Icon } from "@/components/ui/icon";

function PhonePreview({ raw }: { raw: string }) {
  const parsed = parsePhoneNumberFromString(raw);
  let formatted = raw;
  if (parsed && parsed.country) {
    formatted = `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  } else if (parsed) {
    formatted = parsed.formatInternational();
  }
  const country = parsed?.country;
  const Flag = country ? flags[country as keyof typeof flags] : null;
  return (
    <span className="flex min-w-0 items-center gap-1.5 truncate text-xs">
      {Flag && (
        <span className="flex h-3 w-4.5 shrink-0 overflow-hidden bg-foreground/20 [&_svg:not([class*='size-'])]:size-full">
          <Flag title={country!} />
        </span>
      )}
      <span className="truncate">{formatted}</span>
    </span>
  );
}

export function PersonalInfoSection({
  profile,
  revealAction = "reveal",
}: {
  profile: CandidateProfile;
  /** "reveal" for search-sourced profiles, "enrich" for imported / ATS. */
  revealAction?: "reveal" | "enrich";
}) {
  const [showReview, setShowReview] = useState(false);
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);

  const profileId = profile.profile_id;
  const { data: contactData } = useProfileContacts(profileId);
  const revealMutation = useRevealContact(profileId);

  const revealed = contactData?.already_revealed ?? false;
  const allContacts = contactData?.contacts ?? [];

  const localEmails = allContacts.filter(
    (c) => c.type === "email" && c.isLocal,
  );
  const localPhones = allContacts.filter(
    (c) => c.type === "phone" && c.isLocal,
  );

  const personalEmails = allContacts.filter(
    (c) => c.type === "email" && !c.isLocal && c.emailType === "personal",
  );
  const professionalEmails = allContacts.filter(
    (c) => c.type === "email" && !c.isLocal && c.emailType === "professional",
  );
  const remotePhones = allContacts.filter(
    (c) => c.type === "phone" && !c.isLocal,
  );

  // Preview pills should surface ANY contact we have — revealed (remote)
  // or manually contributed / resume-imported (local). Prefer revealed
  // entries first, then fall back to local so an unrevealed profile that
  // arrived with a `source="upload"` email/phone still shows the value.
  const allEmails = useMemo(
    () => [...personalEmails, ...professionalEmails, ...localEmails],
    [personalEmails, professionalEmails, localEmails],
  );
  const allPhones = useMemo(
    () => [...remotePhones, ...localPhones],
    [remotePhones, localPhones],
  );

  const previewEmail = useMemo(
    () =>
      allEmails.length > 0
        ? { item: allEmails[0], otherCount: allEmails.length - 1 }
        : null,
    [allEmails],
  );

  const previewPhone = useMemo(
    () =>
      allPhones.length > 0
        ? { item: allPhones[0], otherCount: allPhones.length - 1 }
        : null,
    [allPhones],
  );

  const handleReveal = async () => {
    await revealMutation.mutateAsync();
    setShowRevealConfirm(false);
    setShowReview(true);
  };

  const emailRow = (value: React.ReactNode) => (
    <ContactLayout.Container>
      <ContactLayout.Item>
        <Icon name="IconEmail1" size={16} className="text-foreground/40" />
        Email Address
      </ContactLayout.Item>
      <ContactLayout.Value>{value}</ContactLayout.Value>
    </ContactLayout.Container>
  );

  const phoneRow = (value: React.ReactNode) => (
    <ContactLayout.Container>
      <ContactLayout.Item>
        <Icon
          name="IconPhoneDynamicIsland"
          size={16}
          className="text-foreground/40"
        />
        Phone Number
      </ContactLayout.Item>
      <ContactLayout.Value>{value}</ContactLayout.Value>
    </ContactLayout.Container>
  );

  const emailPill = previewEmail ? (
    <Button
      variant={"linear-aqua"}
      type="button"
      onClick={() => setShowReview(true)}
      size="xs"
      className="min-w-0 shrink truncate lg:max-w-fit lg:shrink-0"
    >
      <span className="min-w-0 truncate text-xs">
        {previewEmail.item.value}
      </span>
      {previewEmail.otherCount > 0 && (
        <span className="ml-1 shrink-0 text-xs">
          (+{previewEmail.otherCount})
        </span>
      )}
    </Button>
  ) : null;

  const phonePill = previewPhone ? (
    <Button
      variant={"linear-aqua"}
      type="button"
      onClick={() => setShowReview(true)}
      size="xs"
      className="min-w-0 shrink truncate lg:max-w-fit lg:shrink-0"
    >
      <PhonePreview raw={previewPhone.item.value ?? ""} />
      {previewPhone.otherCount > 0 && (
        <span className="ml-1 shrink-0 text-xs">
          (+{previewPhone.otherCount})
        </span>
      )}
    </Button>
  ) : null;

  return (
    <>
      <SectionCard
        title="Personal Information"
        headerRight={
          <button
            type="button"
            onClick={() => setShowReview(true)}
            className="flex shrink-0 gap-0.5 items-center justify-center transition cursor-pointer active:scale-98 text-foreground/50 hover:text-foreground overflow-clip rounded-md min-h-9 px-1 -mr-1 sm:min-h-0 sm:px-0 sm:mr-0"
          >
            <span className="hidden text-sm leading-5 px-0.5 md:block">
              View All Contact Information
            </span>
            <span className="text-sm leading-5 px-0.5 md:hidden">Contacts</span>
            <Icon name="IconChevronRight" size={16} />
          </button>
        }
      >
        <SectionBody>
          <div className="flex flex-col gap-4 items-start w-full">
            {revealed ? (
              <>
                {emailRow(
                  previewEmail ? (
                    emailPill
                  ) : (
                    <ContactManualAdd onClick={() => setShowReview(true)} />
                  ),
                )}
                {phoneRow(
                  previewPhone ? (
                    phonePill
                  ) : (
                    <ContactManualAdd onClick={() => setShowReview(true)} />
                  ),
                )}
              </>
            ) : (
              <>
                {/* Surface manually added / imported contacts even before a
                    reveal — they're real data the user contributed. */}
                {previewEmail && emailRow(emailPill)}
                {previewPhone && phoneRow(phonePill)}
                <ContactLayout.Container>
                  <ContactLayout.Item>
                    <Icon
                      name="IconContacts"
                      size={16}
                      className="text-foreground/40"
                    />
                    Contact Information
                  </ContactLayout.Item>
                  <ContactLayout.Value>
                    <CandidateContactRevealTrigger
                      className="w-auto"
                      action={revealAction}
                      onClick={() => setShowRevealConfirm(true)}
                    />
                  </ContactLayout.Value>
                </ContactLayout.Container>
              </>
            )}
          </div>
        </SectionBody>
      </SectionCard>

      <ContactRevealConfirmAction
        open={showRevealConfirm}
        onOpenChange={setShowRevealConfirm}
        onConfirm={handleReveal}
        loading={revealMutation.isPending}
        action={revealAction}
        candidate={{
          name: profile.full_name ?? "",
          jobTitle: profile.job_title,
          company: profile.job_company_name,
          avatarUrl: profile.avatar_url,
        }}
      />
      <ContactReviewModal
        profileId={profileId}
        open={showReview}
        onOpenChange={setShowReview}
        revealAction={revealAction}
        candidate={{
          name: profile.full_name ?? "",
          jobTitle: profile.job_title,
          company: profile.job_company_name,
          avatarUrl: profile.avatar_url,
        }}
      />
    </>
  );
}
